import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Avatar,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import InsightsIcon from '@mui/icons-material/Insights';
import MarketIcon from '@mui/icons-material/Public';
import PremiumIcon from '@mui/icons-material/WorkspacePremium';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoIcon from '@mui/icons-material/Info';
import GrowthIcon from '@mui/icons-material/Timeline';
import GlobalIcon from '@mui/icons-material/Language';
import ExportIcon from '@mui/icons-material/LocalShipping';
import BankIcon from '@mui/icons-material/AccountBalance';
import SpeedIcon from '@mui/icons-material/Speed';
import SustainabilityIcon from '@mui/icons-material/Grass';
import CarbonIcon from '@mui/icons-material/Co2';
import WaterIcon from '@mui/icons-material/WaterDrop';
import { useShop } from '../../context/ShopContext';
import { DataService } from '../../services/dataService';

const WealthCenter: React.FC = () => {
  const navigate = useNavigate();
  const { activeShop, shops } = useShop();
  const [marketPrices, setMarketPrices] = useState<any[]>([]);
  const [revenue, setRevenue] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Simulated Credit Score Logic
  const creditScore = activeShop ? 720 : 0; // Baseline
  const creditLimit = revenue > 0 ? Math.round(revenue * 1.5) : 50000;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // 1. Fetch Market Data
      const counties = await DataService.getAllCounties();
      const allPrices: any[] = [];
      counties.forEach(c => {
        if (c.marketPrices) {
          c.marketPrices.forEach(p => {
            allPrices.push({ ...p, county: c.name });
          });
        }
      });
      setMarketPrices(allPrices.slice(0, 6));

      // 2. Fetch Real Shop Revenue
      if (activeShop) {
        const totalRevenue = await DataService.getShopRevenue(activeShop.id);
        setRevenue(totalRevenue);
      }

      setLoading(false);
    };

    fetchData();
  }, [activeShop]);

  if (!activeShop) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <MarketIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>Select a Shop to View Wealth Insights</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          You need an active shop to access the Market Intelligence and Wealth Dashboard.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Wealth Center <PremiumIcon color="warning" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Financial performance and market intelligence for <strong>{activeShop.name}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="warning"
          startIcon={<PremiumIcon />}
          sx={{ borderRadius: '12px', fontWeight: 'bold' }}
        >
          Upgrade to Pro
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Financial Overview Cards */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', bgcolor: '#1a237e', color: 'white' }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)' }}>Total Revenue</Typography>
                <MoneyIcon sx={{ color: '#ffd700' }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>KSh {revenue.toLocaleString()}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: '#4caf50' }} />
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 'bold' }}>+12.5% from last month</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%' }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" color="text.secondary">Net Profit Margin</Typography>
                <InsightsIcon color="primary" />
              </Box>
              <Typography variant="h3" fontWeight="bold">24.8%</Typography>
              <LinearProgress variant="determinate" value={24.8} sx={{ height: 8, borderRadius: 5 }} />
              <Typography variant="caption" color="text.secondary">Industry average: 18.2%</Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', border: '2px solid', borderColor: 'warning.light', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: -10, right: -10, opacity: 0.1 }}>
              <PremiumIcon sx={{ fontSize: 100 }} />
            </Box>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight="bold">Wealth Projection</Typography>
              <Typography variant="h4" color="warning.dark">KSh 1.2M</Typography>
              <Typography variant="body2" color="text.secondary">Estimated annual earnings based on current growth trends.</Typography>
              <Button size="small" variant="text" endIcon={<ArrowForwardIcon />}>Detailed Forecast</Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Market Intelligence Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Market Price Intelligence</Typography>
              <Tooltip title="Real-time prices from various county markets">
                <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={2}>
              {marketPrices.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                    <CardContent sx={{ py: '12px !important' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">{item.commodity}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.county}</Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body1" fontWeight="bold" color="primary.main">
                            KSh {item.price} / {item.unit}
                          </Typography>
                          <Chip label="Trending Up" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '10px' }} />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: '12px', textAlign: 'center' }}>
              <Typography variant="body2" gutterBottom>Want to see prices for all 47 counties?</Typography>
              <Button variant="outlined" color="warning" size="small">Unlock Full Market Map</Button>
            </Box>
          </Paper>
        </Grid>

        {/* Wealth Optimization Insights */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Wealth Insights</Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ bgcolor: 'success.light', p: 1, borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                  <GrowthIcon sx={{ color: 'success.dark' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">High Demand Alert</Typography>
                  <Typography variant="body2" color="text.secondary">Demand for "Organic Fertilizers" is up 40% in Kiambu. List your stock now.</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ bgcolor: 'info.light', p: 1, borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon sx={{ color: 'info.dark' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">Cost Optimization</Typography>
                  <Typography variant="body2" color="text.secondary">Switching to "Fast Transport Ltd" could save you KSh 5,000/month in logistics.</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ bgcolor: 'warning.light', p: 1, borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                  <InsightsIcon sx={{ color: 'warning.dark' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">Inventory Intelligence</Typography>
                  <Typography variant="body2" color="text.secondary">You are losing KSh 12,000/week due to out-of-stock "Hybrid Seeds".</Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Farmer Credit Score & Fintech Section */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'primary.light', bgcolor: 'primary.50' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="primary.dark">Farmer Credit Score</Typography>
                <Typography variant="body2" color="text.secondary">Based on fulfillment, growth, and reviews</Typography>
              </Box>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={(creditScore / 850) * 100} size={80} thickness={5} color="success" />
                <Box
                  sx={{
                    top: 0, left: 0, bottom: 0, right: 0,
                    position: 'absolute', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Typography variant="h6" component="div" fontWeight="bold">{creditScore}</Typography>
                </Box>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Loan Eligibility</Typography>
                <Typography variant="h6" fontWeight="bold">KSh {creditLimit.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6} textAlign="right">
                <Button variant="contained" color="success" size="small" startIcon={<BankIcon />} sx={{ borderRadius: '8px' }}>
                  Get Instant Credit
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Financial Growth Report</Typography>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Order Fulfillment Rate</Typography>
                <Typography variant="body2" fontWeight="bold">98.5%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={98.5} color="success" sx={{ height: 6, borderRadius: 5 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Customer Retention</Typography>
                <Typography variant="body2" fontWeight="bold">42%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={42} color="primary" sx={{ height: 6, borderRadius: 5 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">Market Competitiveness</Typography>
                <Typography variant="body2" fontWeight="bold">High</Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Logistics & Fulfillment Efficiency Section */}
      <Box sx={{ mt: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <ExportIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Logistics & Fulfillment Efficiency</Typography>
          <Chip label="Smart Logistics" size="small" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Average Delivery Time</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" fontWeight="bold">1.8 Days</Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold">-12% vs last month</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Elite Performance Status</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Logistics Cost Efficiency</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" fontWeight="bold">92%</Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold">+5% optimization</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Using HUB Optimized Routes</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Last Mile Accuracy</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" fontWeight="bold">99.2%</Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold">Top 1% of Sellers</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Successful first-attempt deliveries</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Climate Impact & Carbon Credits Section */}
      <Box sx={{ mt: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <SustainabilityIcon color="success" />
          <Typography variant="h5" fontWeight="bold">Climate Impact & Carbon Credits</Typography>
          <Chip label="Green Finance" size="small" color="success" sx={{ fontWeight: 'bold' }} />
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'success.light', bgcolor: 'success.50' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main' }}><CarbonIcon /></Avatar>
                <Box>
                  <Typography variant="subtitle2" color="success.dark">Carbon Sequestrated</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.dark">12.4 Tonnes</Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">Equivalent to planting <strong>560 trees</strong> this year.</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'info.main' }}><WaterIcon /></Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Water Saved</Typography>
                  <Typography variant="h4" fontWeight="bold">450,000 L</Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">Using HUB Smart Irrigation recommendations.</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '16px', border: '2px solid', borderColor: 'warning.light', position: 'relative' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Carbon Credit Value</Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.dark">KSh 32,500</Typography>
              <Button 
                fullWidth 
                variant="contained" 
                color="warning" 
                sx={{ mt: 2, fontWeight: 'bold' }}
                onClick={() => navigate('/carbon-credits')}
              >
                Sell Credits Now
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Global Market Opportunities */}
      <Box sx={{ mt: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <GlobalIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Global Market Opportunities</Typography>
          <Chip label="Export Ready" size="small" color="success" variant="filled" sx={{ fontWeight: 'bold' }} />
        </Stack>
        
        <Grid container spacing={3}>
          {[
            { crop: 'Avocados', destination: 'European Union', premium: '+45%', status: 'High Demand' },
            { crop: 'Macadamia Nuts', destination: 'China', premium: '+30%', status: 'Rising' },
            { crop: 'Specialty Coffee', destination: 'USA', premium: '+60%', status: 'Elite' },
            { crop: 'Fresh Flowers', destination: 'Netherlands', premium: '+25%', status: 'Stable' },
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid', borderColor: 'divider', '&:hover': { boxShadow: 3 } }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>{item.destination}</Typography>
                <Typography variant="h6" fontWeight="bold">{item.crop}</Typography>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="success.main" fontWeight="bold">{item.premium} Price Premium</Typography>
                  <ExportIcon fontSize="small" color="action" />
                </Stack>
                <Chip label={item.status} size="small" sx={{ mt: 1, height: 20, fontSize: '10px' }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        <Paper sx={{ mt: 3, p: 3, borderRadius: '16px', bgcolor: '#263238', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>Want to export your products?</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Our AI logistics engine handles compliance, shipping, and global payments for you.</Typography>
          </Box>
          <Button variant="contained" color="primary" sx={{ borderRadius: '12px', px: 4, fontWeight: 'bold' }}>Get Export Certified</Button>
        </Paper>
      </Box>
    </Box>
  );
};

export default WealthCenter;
